import { Link } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useClientData } from '../ClientLayout.jsx';
import { useAuth } from '../../auth/AuthContext.jsx';
import { api } from '../../lib/api.js';
import { getLocalSettings } from '../../lib/settingsStore.js';
import { saveMatterPatch } from '../../lib/localMatterPatches.js';
import {
  buildEvidenceVaultUsage,
  collectEvidenceVaultFiles,
  downloadEvidenceLockerFiles,
  forgetRetainedEvidenceFiles,
  formatStorageBytes,
  removeEvidenceFileFromMatter,
  removeEvidenceFilesFromMatters,
  resolveEvidenceLockerFileUrl,
  selectedEvidenceFilesFromIds,
  sumEvidenceVaultBytes,
} from '../../lib/evidenceVault.js';
import {
  FileTypeIcon,
  RemoveFileButton,
  ViewableFileName,
} from '../EvidenceUpload.jsx';
import ListPagination, { useListPagination } from '../ListPagination.jsx';

/** Settings → Billing, opened on the Evidence Locker plan tab. */
const LOCKER_BILLING_TO = '/client/settings?tab=billing&billingTab=evidence-locker';

function BulkActionTip({ label, text }) {
  return (
    <span className="list-bulk-tip kanban-stage-tip">
      <button
        type="button"
        className="kanban-stage-tip-btn"
        tabIndex={0}
        aria-label={`${label}: ${text}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden>
          <circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <path
            d="M8 7.2v3.6M8 5.2h.01"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>
      <span className="kanban-stage-tip-bubble list-bulk-tip-bubble" role="tooltip">
        {text}
      </span>
    </span>
  );
}

const BULK_ACTION_TIPS = {
  download:
    'Download the selected evidence files. One file downloads directly; multiple files download as a ZIP.',
  delete:
    'Permanently delete the selected evidence files from Evidence Locker and their related demands.',
};

export default function EvidenceVaultPage() {
  const { allMatters, evidenceMatters, setMatters, notice, setNotice } = useClientData();
  const lockerMatters = evidenceMatters || allMatters;
  const { user } = useAuth();
  const [billing] = useState(() => getLocalSettings(user?.email || '').billing || {});
  const [pendingDeleteId, setPendingDeleteId] = useState('');
  const [viewerUrls, setViewerUrls] = useState({});
  const [selected, setSelected] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const bulkMenuRef = useRef(null);

  const files = useMemo(() => collectEvidenceVaultFiles(lockerMatters), [lockerMatters]);
  const usedBytes = useMemo(() => sumEvidenceVaultBytes(files), [files]);
  const usage = useMemo(
    () => buildEvidenceVaultUsage({ usedBytes, billing }),
    [usedBytes, billing]
  );
  const pagination = useListPagination(files, { storageKey: 'evidence-locker' });
  const pageFiles = pagination.pageItems;
  const fileKey = useMemo(
    () => files.map((file) => `${file.id}:${file.url || ''}`).join('\u0000'),
    [files]
  );

  useEffect(() => {
    let cancelled = false;
    const created = [];

    if (!files.length) {
      setViewerUrls({});
      return undefined;
    }

    (async () => {
      const next = {};
      for (const file of files) {
        const url = await resolveEvidenceLockerFileUrl(file);
        if (!url) continue;
        if (String(url).startsWith('blob:')) created.push(url);
        next[file.id] = url;
      }
      if (!cancelled) setViewerUrls(next);
    })();

    return () => {
      cancelled = true;
      for (const url of created) {
        try {
          URL.revokeObjectURL(url);
        } catch {
          // ignore
        }
      }
    };
  }, [fileKey, files]);

  useEffect(() => {
    setSelected(new Set());
    setBulkOpen(false);
  }, [files, pagination.page, pagination.pageSize]);

  useEffect(() => {
    if (!selected.size) setBulkOpen(false);
  }, [selected.size]);

  useEffect(() => {
    if (!bulkOpen) return undefined;
    function onDocClick(e) {
      if (bulkMenuRef.current && !bulkMenuRef.current.contains(e.target)) {
        setBulkOpen(false);
      }
    }
    function onKey(e) {
      if (e.key === 'Escape') setBulkOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [bulkOpen]);

  function toggleOne(id, checked) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(String(id));
      else next.delete(String(id));
      return next;
    });
  }

  function toggleSelectAll(checked) {
    if (!checked) {
      setSelected(new Set());
      return;
    }
    setSelected(new Set(pageFiles.map((file) => String(file.id))));
  }

  function persistMatterPatch(nextMatter, historySummary) {
    // Persist deletes locally so demo/seed rows and stale API responses cannot
    // resurrect Evidence Locker files after refresh.
    saveMatterPatch(nextMatter.ROWID, nextMatter);
    setMatters((prev) =>
      prev.map((m) => (String(m.ROWID) === String(nextMatter.ROWID) ? { ...m, ...nextMatter } : m))
    );
    // Never block the UI on the API — a hung PUT used to leave every delete button disabled.
    void api
      .put(`/server/client_api/matters/${encodeURIComponent(nextMatter.ROWID)}`, {
        ...nextMatter,
        lockerEvidenceDelete: true,
        historySummary,
        historyType: 'EVIDENCE_UPDATED',
      })
      .catch(() => {
        // Demo / locked-server paths still keep the local locker update above.
      });
  }

  function fileStillOnMatter(matter, file) {
    return collectEvidenceVaultFiles([matter]).some(
      (row) =>
        String(row.id) === String(file.id) ||
        (row.source === file.source &&
          String(row.name).toLowerCase() === String(file.name || '').toLowerCase())
    );
  }

  function deleteLockerFile(file) {
    if (!file?.matterId || bulkBusy) return;
    const ok = window.confirm(
      `Delete “${file.name}” from Evidence Locker? This removes it from the related demand and frees storage.`
    );
    if (!ok) return;

    const matter = lockerMatters.find((m) => String(m.ROWID) === String(file.matterId));
    if (!matter) {
      forgetRetainedEvidenceFiles([file]);
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(String(file.id));
        return next;
      });
      setNotice(`Deleted “${file.name}” from Evidence Locker.`);
      return;
    }

    const nextMatter = removeEvidenceFileFromMatter(matter, file);
    if (fileStillOnMatter(nextMatter, file)) {
      setNotice(
        `Could not remove “${file.name}”. Open the related demand and remove it from Evidence there.`
      );
      return;
    }

    setPendingDeleteId(file.id);
    forgetRetainedEvidenceFiles([file]);
    persistMatterPatch(
      nextMatter,
      `Removed evidence file “${file.name}” from Evidence Locker`
    );
    setSelected((prev) => {
      const next = new Set(prev);
      next.delete(String(file.id));
      return next;
    });
    setNotice(`Deleted “${file.name}” from Evidence Locker.`);
    // Clear row busy state on the next tick so the list can re-render without the file.
    queueMicrotask(() => setPendingDeleteId(''));
  }

  async function handleBulkDownload(rows) {
    if (!rows.length || bulkBusy) return;
    setBulkBusy(true);
    setNotice(
      rows.length === 1
        ? 'Preparing download…'
        : `Preparing ZIP of ${rows.length} files…`
    );
    try {
      const result = await downloadEvidenceLockerFiles(rows, { urlById: viewerUrls });
      if (!result.downloaded) {
        setNotice('Could not download the selected files. Try opening them individually.');
        return;
      }
      if (result.zip) {
        setNotice(
          result.skipped
            ? `Downloaded ZIP with ${result.downloaded} file${result.downloaded === 1 ? '' : 's'} (${result.skipped} skipped).`
            : `Downloaded ZIP with ${result.downloaded} files.`
        );
      } else {
        setNotice(
          result.skipped
            ? `Downloaded 1 file (${result.skipped} skipped).`
            : 'Downloaded 1 file.'
        );
      }
    } catch {
      setNotice('Download failed. Try again or open files individually.');
    } finally {
      setBulkBusy(false);
    }
  }

  function handleBulkDelete(rows) {
    if (!rows.length || bulkBusy) return;
    const ok = window.confirm(
      `Delete ${rows.length} selected file${rows.length === 1 ? '' : 's'} from Evidence Locker? This removes them from related demands and frees storage.`
    );
    if (!ok) return;

    setBulkBusy(true);
    setBulkOpen(false);
    const { matters: patched, removedCount } = removeEvidenceFilesFromMatters(lockerMatters, rows);
    forgetRetainedEvidenceFiles(rows);
    const patchedIds = new Set(patched.map((matter) => String(matter.ROWID)));
    const retainedOnly = rows.filter((file) => !patchedIds.has(String(file.matterId))).length;
    for (const nextMatter of patched) {
      const names = rows
        .filter((file) => String(file.matterId) === String(nextMatter.ROWID))
        .map((file) => file.name)
        .filter(Boolean);
      persistMatterPatch(
        nextMatter,
        names.length === 1
          ? `Removed evidence file “${names[0]}” from Evidence Locker`
          : `Removed ${names.length} evidence files from Evidence Locker`
      );
    }
    setSelected(new Set());
    setBulkBusy(false);
    const removedTotal = removedCount + retainedOnly;
    setNotice(
      removedTotal
        ? `Deleted ${removedTotal} file${removedTotal === 1 ? '' : 's'} from Evidence Locker.`
        : 'No files were deleted.'
    );
  }

  function runBulk(action) {
    const rows = selectedEvidenceFilesFromIds(files, selected);
    setBulkOpen(false);
    action?.(rows);
  }

  const pageIds = pageFiles.map((file) => String(file.id));
  const selectedOnPage = pageIds.filter((id) => selected.has(id)).length;
  const allSelected = pageIds.length > 0 && selectedOnPage === pageIds.length;
  const someSelected = selectedOnPage > 0 && selectedOnPage < pageIds.length;
  const meterPct = Math.min(100, Math.round((usage.ratio || 0) * 100));
  const busy = bulkBusy;

  return (
    <div className="client-page">
      <div className="section-head">
        <div>
          <h1>Evidence Locker</h1>
          <p>
            Secure storage for invoices, evidence packs, and demand documents. Delete files anytime
            to free space, or upgrade your locker plan in Billing.
          </p>
        </div>
        <div className="section-head-actions">
          <Link className="btn-ghost" to={LOCKER_BILLING_TO}>
            Billing
          </Link>
        </div>
      </div>

      {notice ? <p className="inline-notice">{notice}</p> : null}
      {usage.atWarn && !usage.atLimit ? (
        <p className="inline-notice">
          Locker is {meterPct}% full. Delete files below or upgrade storage in Billing before
          uploads are blocked.
        </p>
      ) : null}
      {usage.atLimit ? (
        <p className="inline-notice">
          Locker is full. Delete files below or upgrade your Evidence Locker plan to upload more.
        </p>
      ) : null}

      <div className="vault-usage-card">
        <div className="vault-usage-head">
          <div>
            <strong>{usage.tierLabel}</strong>
            <p className="muted-line">{usage.label}</p>
          </div>
          <span className={usage.atLimit ? 'conn-badge' : 'conn-badge connected'}>
            {formatStorageBytes(usage.usedBytes)} / {usage.gbLimit} GB
          </span>
        </div>
        <div
          className={`vault-usage-meter${usage.atWarn ? ' is-warn' : ''}${
            usage.atLimit ? ' is-full' : ''
          }`}
          role="progressbar"
          aria-valuenow={meterPct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Evidence Locker storage used"
        >
          <span style={{ width: `${meterPct}%` }} />
        </div>
        <p className="muted-line vault-usage-billing-hint">
          Need more space? Change your locker plan in{' '}
          <Link className="text-link strong" to={LOCKER_BILLING_TO}>
            Billing
          </Link>
          .
        </p>
      </div>

      <div className="list-view-toolbar">
        <p className="list-view-count">
          {files.length} file{files.length === 1 ? '' : 's'} in locker
          {selected.size ? ` · ${selected.size} selected` : ''}
        </p>
        {selected.size ? (
          <div className="list-bulk-menu" ref={bulkMenuRef}>
            <button
              type="button"
              className="btn-ghost list-bulk-toggle"
              aria-expanded={bulkOpen}
              aria-haspopup="menu"
              disabled={busy}
              onClick={() => setBulkOpen((open) => !open)}
            >
              Bulk Actions
              <span aria-hidden>▾</span>
            </button>
            {bulkOpen ? (
              <div className="list-bulk-dropdown" role="menu" aria-label="Bulk evidence actions">
                <div className="list-bulk-item-row" role="none">
                  <button
                    type="button"
                    role="menuitem"
                    className="list-bulk-item"
                    onClick={() => runBulk(handleBulkDownload)}
                  >
                    Download
                  </button>
                  <BulkActionTip label="Download" text={BULK_ACTION_TIPS.download} />
                </div>
                <div className="list-bulk-item-row" role="none">
                  <button
                    type="button"
                    role="menuitem"
                    className="list-bulk-item is-danger"
                    onClick={() => runBulk(handleBulkDelete)}
                  >
                    Delete
                  </button>
                  <BulkActionTip label="Delete" text={BULK_ACTION_TIPS.delete} />
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {files.length ? (
        <>
          <div className="debtor-table-wrap">
            <table className="matters-table debtor-table evidence-locker-table">
              <thead>
                <tr>
                  <th className="list-col-check">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someSelected;
                      }}
                      onChange={(e) => toggleSelectAll(e.target.checked)}
                      aria-label="Select all evidence files on this page"
                    />
                  </th>
                  <th>File</th>
                  <th>Type</th>
                  <th>Debtor</th>
                  <th>Demand ID</th>
                  <th>Size</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageFiles.map((file) => {
                  const demandHref = `/client/demands/${encodeURIComponent(file.matterId)}`;
                  const id = String(file.id);
                  const isSelected = selected.has(id);
                  const deleting = pendingDeleteId === file.id;
                  return (
                    <tr
                      key={file.id}
                      className={`matter-row${isSelected ? ' is-selected' : ''}`}
                    >
                      <td
                        className="list-col-check"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => toggleOne(id, e.target.checked)}
                          aria-label={`Select ${file.name}`}
                        />
                      </td>
                      <td>
                        <div className="evidence-locker-file-cell">
                          <FileTypeIcon name={file.name} />
                          <ViewableFileName name={file.name} url={viewerUrls[file.id] || null} />
                        </div>
                      </td>
                      <td>{file.kind}</td>
                      <td>
                        {file.demandDeleted ? (
                          file.debtorName || file.matterLabel
                        ) : (
                          <Link
                            className="list-column-link"
                            to={demandHref}
                            state={{ from: '/client/evidence-locker' }}
                          >
                            {file.debtorName || file.matterLabel}
                          </Link>
                        )}
                      </td>
                      <td>
                        {file.demandId && !file.demandDeleted ? (
                          <Link
                            className="list-column-link"
                            to={demandHref}
                            state={{ from: '/client/evidence-locker' }}
                          >
                            {file.demandId}
                          </Link>
                        ) : (
                          file.demandId || '—'
                        )}
                      </td>
                      <td>{formatStorageBytes(file.bytes)}</td>
                      <td>
                        <RemoveFileButton
                          label={deleting ? `Deleting ${file.name}` : `Delete ${file.name}`}
                          disabled={busy || deleting}
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteLockerFile(file);
                          }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <ListPagination
            page={pagination.page}
            pageCount={pagination.pageCount}
            pageSize={pagination.pageSize}
            onPageSizeChange={pagination.setPageSize}
            rangeStart={pagination.rangeStart}
            rangeEnd={pagination.rangeEnd}
            canPrev={pagination.canPrev}
            canNext={pagination.canNext}
            onPrev={pagination.goPrev}
            onNext={pagination.goNext}
          />
        </>
      ) : (
        <div className="empty-state">
          No evidence files yet. Upload invoices and evidence from a demand record.
        </div>
      )}
    </div>
  );
}
